import {
  firstFlaggedPage,
  flagText,
  isFlagged,
  reviewPages,
  type ReviewDish,
  type ReviewPage,
} from '@/features/menu/utils/menu-import-review';
import { useT } from '@/i18n';
import { formatMoney } from '@/utils/money';
import type { MenuImportReview } from '@restaurant/api-client';
import { Block, Button, List, ListItem, Segmented } from 'framework7-react';
import { useState } from 'react';

function SourceMissing() {
  const c = useT().menuImportReview;
  return (
    <div className="menu-import-review__missing" data-testid="import-review-source-missing">
      <i className="f7-icons" aria-hidden="true">
        doc_text_viewfinder
      </i>
      <span>{c.sourceMissing}</span>
    </div>
  );
}

function SourceView({ source }: { source: ReviewPage }) {
  const c = useT().menuImportReview;
  const [broken, setBroken] = useState<string | null>(null);
  if (source.kind === 'text') {
    return source.text === null ? (
      <SourceMissing />
    ) : (
      <pre className="menu-import-review__text" data-testid="import-review-source-text">
        {source.text}
      </pre>
    );
  }
  if (source.url === null || broken === source.url) return <SourceMissing />;
  return (
    <>
      {source.kind === 'image' ? (
        <img
          className="menu-import-review__image"
          src={source.url}
          alt={source.label}
          onError={() => setBroken(source.url)}
          data-testid="import-review-source-image"
        />
      ) : (
        <iframe
          className="menu-import-review__pdf"
          src={source.url}
          title={source.label}
          data-testid="import-review-source-pdf"
        />
      )}
      <a
        className="menu-import-review__open"
        href={source.url}
        target="_blank"
        rel="noreferrer"
        data-testid="import-review-source-open"
      >
        {c.openOriginal}
      </a>
    </>
  );
}

function DishRow({
  dish,
  currency,
  copy: c,
  onOpen,
}: {
  dish: ReviewDish;
  currency: string;
  copy: ReturnType<typeof useT>['menuImportReview'];
  onOpen: (dish: ReviewDish) => void;
}) {
  return (
    <ListItem
      key={dish.productId}
      link="#"
      title={dish.name}
      after={dish.price > 0 ? formatMoney(dish.price, currency) : undefined}
      onClick={(e: { preventDefault?: () => void }) => {
        e.preventDefault?.();
        onOpen(dish);
      }}
      data-testid={`import-review-dish-${dish.productId}`}
    >
      {isFlagged(dish) && (
        <span
          slot="footer"
          className="menu-item-issue"
          data-testid={`import-review-flags-${dish.productId}`}
        >
          <i className="f7-icons" aria-hidden="true">
            exclamationmark_triangle_fill
          </i>
          {dish.flags.map((flag) => flagText(flag, c, currency)).join(' · ')}
        </span>
      )}
    </ListItem>
  );
}

export function ImportReviewView({
  review,
  live,
  currency,
  onOpenDish,
}: {
  review: MenuImportReview;
  live: boolean;
  currency: string;
  onOpenDish: (dish: ReviewDish) => void;
}) {
  const c = useT().menuImportReview;
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const [pane, setPane] = useState<'page' | 'dishes'>('dishes');
  const pages = reviewPages(review, flaggedOnly);
  const [index, setIndex] = useState(() => firstFlaggedPage(reviewPages(review, false)));
  const current = pages[Math.min(index, pages.length - 1)];
  if (!current) return null;
  const position = pages.indexOf(current);

  return (
    <div
      className={`menu-import-review menu-import-review--show-${pane}`}
      data-testid="import-review"
    >
      {live && (
        <Block className="menu-import-review__note" data-testid="import-review-live">
          {c.liveNote}
        </Block>
      )}
      <div className="menu-import-review__bar">
        <div className="menu-import-review__pager">
          <button
            type="button"
            className="ai-dock__step"
            disabled={position === 0}
            onClick={() => setIndex(position - 1)}
            aria-label={c.previousPage}
            data-testid="import-review-prev"
          >
            <i className="f7-icons" aria-hidden="true">
              chevron_left
            </i>
          </button>
          <span className="menu-import-review__position" data-testid="import-review-position">
            {c.page(position + 1, pages.length)}
            {current.flagged > 0 && ` · ${c.flaggedCount(current.flagged)}`}
          </span>
          <button
            type="button"
            className="ai-dock__step"
            disabled={position >= pages.length - 1}
            onClick={() => setIndex(position + 1)}
            aria-label={c.nextPage}
            data-testid="import-review-next"
          >
            <i className="f7-icons" aria-hidden="true">
              chevron_right
            </i>
          </button>
        </div>
        <Segmented strong round className="menu-import-review__filter">
          <Button
            active={!flaggedOnly}
            onClick={() => setFlaggedOnly(false)}
            data-testid="import-review-filter-all"
          >
            {c.allDishes}
          </Button>
          <Button
            active={flaggedOnly}
            onClick={() => setFlaggedOnly(true)}
            data-testid="import-review-filter-flagged"
          >
            {c.flaggedOnly}
          </Button>
        </Segmented>
        <Segmented strong round className="menu-import-review__toggle">
          <Button
            active={pane === 'page'}
            onClick={() => setPane('page')}
            data-testid="import-review-show-page"
          >
            {c.showPage}
          </Button>
          <Button
            active={pane === 'dishes'}
            onClick={() => setPane('dishes')}
            data-testid="import-review-show-dishes"
          >
            {c.showDishes}
          </Button>
        </Segmented>
      </div>
      <div className="menu-import-review__panes">
        <div className="menu-import-review__source" data-testid="import-review-source">
          <div className="menu-import-review__label">{current.source.label}</div>
          <SourceView key={current.source.page} source={current.source} />
        </div>
        <div className="menu-import-review__dishes" data-testid="import-review-dishes">
          {current.source.sections.length > 0 && (
            <div className="menu-import-review__label">{current.source.sections.join(' · ')}</div>
          )}
          {current.dishes.length === 0 ? (
            <p className="menu-import-review__empty" data-testid="import-review-empty">
              {flaggedOnly && current.total > 0 ? c.noFlagged : c.noDishes}
            </p>
          ) : (
            <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
              {current.dishes.map((dish) =>
                DishRow({ dish, currency, copy: c, onOpen: onOpenDish }),
              )}
            </List>
          )}
        </div>
      </div>
    </div>
  );
}
